<template>
  <PbDialog
    :loading="loading"
    :dialog-title="translate(dialogTitle)"
    :horizontal-scroll="false"
    :vertical-scroll="false"
    :with-style="false"
    :close-to="closeTo"
    class-name="pb-add-dialog"
    size="xlarge"
    @close="cancel"
  >
    <template #afterTitle>
      <slot name="header" />
    </template>
    <slot>
      <div class="pb-add-dialog-panels-wrapper">
        <div
          v-if="slots.left"
          class="pb-add-dialog-panel pb-add-dialog-left-panel"
          :class="{
            [`pb-add-dialog-panel-sm-${smSizes[0]}`]: !!smSizes[0],
            [`pb-add-dialog-panel-md-${mdSizes[0]}`]: !!mdSizes[0],
            [`pb-add-dialog-panel-lg-${lgSizes[0]}`]: !!lgSizes[0],
            [`pb-add-dialog-panel-xl-${xlSizes[0]}`]: !!xlSizes[0],
            [`pb-add-dialog-items-sm-${itemsSizes[0]}`]: itemsPanel === 'left' && !!itemsSizes[0],
            [`pb-add-dialog-items-md-${itemsSizes[1]}`]: itemsPanel === 'left' && !!itemsSizes[1],
            [`pb-add-dialog-items-lg-${itemsSizes[2]}`]: itemsPanel === 'left' && !!itemsSizes[2],
            [`pb-add-dialog-items-xl-${itemsSizes[3]}`]: itemsPanel === 'left' && !!itemsSizes[3],
          }"
        >
          <div
            v-if="leftPanelTitle || slots.leftTitle"
            class="pb-add-dialog-panel-header"
          >
            <slot name="leftTitle">
              {{ leftPanelTitle }}
            </slot>
          </div>
          <ScrollableWrapper>
            <div class="pb-add-dialog-panel-content">
              <slot name="left" />
            </div>
          </ScrollableWrapper>
        </div>
        <div
          v-if="slots.middle"
          class="pb-add-dialog-panel pb-add-dialog-middle-panel"
          :class="{
            [`pb-add-dialog-panel-sm-${smSizes[1]}`]: !!smSizes[1],
            [`pb-add-dialog-panel-md-${mdSizes[1]}`]: !!mdSizes[1],
            [`pb-add-dialog-panel-lg-${lgSizes[1]}`]: !!lgSizes[1],
            [`pb-add-dialog-panel-xl-${xlSizes[1]}`]: !!xlSizes[1],
            [`pb-add-dialog-items-sm-${itemsSizes[0]}`]: itemsPanel === 'middle' && !!itemsSizes[0],
            [`pb-add-dialog-items-md-${itemsSizes[1]}`]: itemsPanel === 'middle' && !!itemsSizes[1],
            [`pb-add-dialog-items-lg-${itemsSizes[2]}`]: itemsPanel === 'middle' && !!itemsSizes[2],
            [`pb-add-dialog-items-xl-${itemsSizes[3]}`]: itemsPanel === 'middle' && !!itemsSizes[3],
          }"
        >
          <div
            v-if="middlePanelTitle || slots.middleTitle"
            class="pb-add-dialog-panel-header"
          >
            <slot name="middleTitle">
              {{ middlePanelTitle }}
            </slot>
          </div>
          <ScrollableWrapper
            interactive
            :vertical-amount="scroll"
            @vertical-scroll="scrolled"
          >
            <div class="pb-add-dialog-panel-content">
              <slot name="middle" />
            </div>
          </ScrollableWrapper>
        </div>
        <div
          v-if="slots.right"
          class="pb-add-dialog-panel pb-add-dialog-right-panel"
          :class="{
            [`pb-add-dialog-panel-sm-${smSizes[2]}`]: !!smSizes[2],
            [`pb-add-dialog-panel-md-${mdSizes[2]}`]: !!mdSizes[2],
            [`pb-add-dialog-panel-lg-${lgSizes[2]}`]: !!lgSizes[2],
            [`pb-add-dialog-panel-xl-${xlSizes[2]}`]: !!xlSizes[2],
            [`pb-add-dialog-items-sm-${itemsSizes[0]}`]: itemsPanel === 'right' && !!itemsSizes[0],
            [`pb-add-dialog-items-md-${itemsSizes[1]}`]: itemsPanel === 'right' && !!itemsSizes[1],
            [`pb-add-dialog-items-lg-${itemsSizes[2]}`]: itemsPanel === 'right' && !!itemsSizes[2],
            [`pb-add-dialog-items-xl-${itemsSizes[3]}`]: itemsPanel === 'right' && !!itemsSizes[3],
          }"
        >
          <div
            v-if="rightPanelTitle || slots.rightTitle"
            class="pb-add-dialog-panel-header"
          >
            <slot name="rightTitle">
              {{ rightPanelTitle }}
            </slot>
          </div>
          <ScrollableWrapper>
            <div class="pb-add-dialog-panel-content">
              <slot name="right" />
            </div>
          </ScrollableWrapper>
        </div>
      </div>
    </slot>

    <template #footer>
      <DialogActions
        :loading="loading"
        :cancel-attrs="{ to: closeTo }"
        :confirm-text="confirmLabel"
        :confirm-disabled="confirmDisabled"
        @cancel="cancel"
        @confirm="confirm"
      />
    </template>
  </PbDialog>
</template>

<script setup>
import {
  computed,
  useSlots,
  watch,
} from 'vue'
import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'
import ScrollableWrapper from '@nicolaudie-group/ng-ui-kit/components/ScrollableWrapper/ScrollableWrapper.vue'
import PbDialog from '@/components/PbDialog.vue'
import useProfileStore from '@/stores/profile.store'
import routeNames from '@/router/names'
import { useObject } from '@/composables/useProfile'
import useTranslate from '@/composables/useTranslate'
import DialogActions from '@/components/DialogActions/DialogActions.vue'

defineOptions({
  compatConfig: {
    MODE: 3, // opt-in to Vue 3 behavior for this component only
  },
  inheritAttrs: true,
})

const profileStore = useProfileStore()

const slots = useSlots()

const { translate } = useTranslate()

const props = defineProps({
  dialogTitle: { type: String, default: 'pb2.addDialog.defaultTitle' },
  loading: { type: Boolean, default: false },
  leftPanelTitle: { type: String, default: '' },
  middlePanelTitle: { type: String, default: '' },
  rightPanelTitle: { type: String, default: '' },
  confirmLabel: { type: String, default: 'pb2.addDialog.defaultConfirmLabel' },
  confirmDisabled: { type: Boolean, default: false },
  scroll: { type: Number, default: 0 },
  itemsPanel: { type: String, default: 'middle' },
  smSizes: { type: Array, default: () => [33, 33, 33] },
  mdSizes: { type: Array, default: () => [25, 50, 25] },
  lgSizes: { type: Array, default: () => [25, 50, 25] },
  xlSizes: { type: Array, default: () => [20, 60, 20] },
  itemsSizes: { type: Array, default: () => [100, 100, 50, 33] },
})

const emit = defineEmits(['add', 'cancel', 'scroll'])

function confirm() {
  emit('add')
}

const object = useObject()

const closeTo = computed(() => {
  const { profileUid } = profileStore
  return object.value ? object.value.link : {
    name: routeNames.GlobalEditor,
    params: {
      profileUid,
    },
  }
})

function cancel() {
  emit('cancel')
}

function scrolled(amount) {
  emit('scroll', amount)
}

const enterHotkeyEnabled = computed(() => !props.loading && !props.confirmDisabled)

const { active: enterPressed } = useHotkeys(KEYS.Enter, {
  shift: false,
  alt: false,
  meta: false,
  ctrl: false,
  exact: true,
  enabled: enterHotkeyEnabled,
  disableInInput: true,
  disableInTextarea: true,
  disableInButton: true,
})

watch(enterPressed, () => {
  if (enterPressed.value) {
    confirm()
  }
})
</script>

<style lang="scss" src="./AddDialog.scss" />
